import {toast} from "react-hot-toast";

// SB-08 maximum reference counts, by selected output format (DocType slot).
export const SB08_PDF_REFERENCE_LIMIT =
    Number(process.env.REACT_APP_JUNE_SB08_PDF_REFERENCES_LIMIT) || 2800;
export const SB08_DOCX_REFERENCE_LIMIT =
    Number(process.env.REACT_APP_JUNE_SB08_DOCX_REFERENCES_LIMIT) || 4000;

/**
 * Returns the maximum allowed SB-08 reference count for the chosen output format.
 *
 * @param {string} docType - The selected DocType slot value ("PDF" or "DOCX").
 * @returns {number|null} The limit, or null when the format is unrecognised.
 */
export const getSb08ReferenceLimit = (docType) => {
    switch ((docType || "").trim().toUpperCase()) {
        case "PDF":
            return SB08_PDF_REFERENCE_LIMIT;
        case "DOCX":
            return SB08_DOCX_REFERENCE_LIMIT;
        default:
            return null;
    }
};

/**
 * Validates the SB-08 reference count against the limit for the selected output
 * format. Shows a toast and returns false when the count exceeds the maximum,
 * preventing generation for that format.
 *
 * @param {number} referenceCount - Number of references the user provided.
 * @param {string} docType - The selected DocType slot value ("PDF" or "DOCX").
 * @returns {boolean} true when generation may proceed, false otherwise.
 */
export const validateSb08ReferenceCount = (referenceCount, docType) => {
    const limit = getSb08ReferenceLimit(docType);
    if (limit === null) {
        return true;
    }
    if (referenceCount > limit) {
        const format = docType.trim().toUpperCase();
        toast.error(
            `${format} generation supports a maximum of ${limit.toLocaleString()} references, but ${referenceCount.toLocaleString()} were provided. ${
                format === "PDF"
                    ? "Please select DOCX or reduce the number of references and try again."
                    : "Please reduce the number of references and try again."
            }`
        );
        return false;
    }
    return true;
};

export const formatInput = (input, intentName) => {
    if (!input) {
        toast.error(
            "No input detected. Please enter the required information and try again."
        );
        return;
    }

    const lines = input.split("\n");
    let entries;

    if (lines.length === 1) {
        const rawEntries = input.split(",");

        const hasInvalid = rawEntries.some((entry) => {
            const simplified = entry.trim().replace(/[^a-zA-Z0-9]/g, "");
            return !simplified;
        });

        if (hasInvalid) {
            toast.error(
                "The application number provided is invalid. Please verify and try again."
            );
            return;
        }

        entries = rawEntries.map((entry) =>
            entry.trim().replace(/[^a-zA-Z0-9]/g, "")
        );
    } else {
        entries = lines
            .filter((line) => line.trim())
            .map((line) => line.replace(/[^a-zA-Z0-9]/g, ""));
    }

    const limitsByIntent = {
        RefCheckIntent: Number(process.env.REACT_APP_JUNE_REF_CHECK_REFERENCES_NUMBER_LIMIT),
        // Allow up to the largest format limit at entry; the format-specific
        // cap is enforced when the DocType (PDF/DOCX) is selected.
        Sb08Intent: SB08_DOCX_REFERENCE_LIMIT,
        ReferenceDownloadIntent: Number(process.env.REACT_APP_JUNE_REFERENCE_DOWNLOAD_NUMBER_LIMIT),
        DEFAULT: Number(process.env.REACT_APP_JUNE_APPLICATION_NUMBER_LIMIT)
    };
    const limit = limitsByIntent[intentName] ?? limitsByIntent.DEFAULT;

    if (entries.length > limit) {
        toast.error(`The count of numbers should not exceed ${limit}.`);
        return;
    }

    if (intentName === "EPOBiblioIntent") {
        const isValid = entries.some((entry) => {
            const trimmed = entry.trim().toUpperCase();

            if (trimmed.startsWith("EP")) {
                return true;
            }

            if (/^\d/.test(trimmed)) {
                return true;
            }

            return false;
        });

        if (!isValid) {
            toast.error("The EP Publication number provided is invalid. Please verify and try again.");
            return;
        }
    }

    if (intentName === "GlobalPatentIntent"){
        const isValid = entries.every((entry) => {
            const trimmed = entry.trim().toUpperCase();
            const regex = /^[A-Z]{2}/;
            return regex.test(trimmed);
        });

        if (!isValid) {
            toast.error("Please enter a valid country code for the publication number provided.");
            return;
        }
    }

    return entries.join(",");
};

// Custom Forms can target EP/Global templates, whose publication numbers
// carry country and kind codes (e.g. "EP1234567A1") that the plain-digit
// stripping below would destroy - so those two intents get this EP-aware
// variant instead, mirroring customforms_handler.py's _normalize_application_number
// / _US_FORMATTED_APPLICATION_NUMBER / _parse_application_numbers exactly, so
// the two stay in sync: a US-formatted number ("90/573,750") still collapses
// to plain digits, but anything else (EP/Global refs, PCT numbers) only loses
// whitespace, keeping its own shape.
const CUSTOM_FORM_INTENTS = ["createcustomformintent", "generatecustomformintent"];
const US_FORMATTED_APPLICATION_NUMBER = /(\d{2})\s*\/\s*(\d{3})\s*,\s*(\d{3})/g;
// Only ever safe between two already-all-digit runs (e.g. two space-separated
// US numbers once the pattern above has collapsed each one's "/" and ",") -
// never next to a letter, which is what keeps a single reference like
// "EP 1234567 A1" (letter-digit / digit-letter boundaries) intact here.
const INTER_NUMBER_WHITESPACE = /(?<=\d)\s+(?=\d)/g;

const normalizeCustomFormApplicationNumber = (entry) => {
    const compact = entry.replace(/[\s/,]+/g, "");
    // Plain digits, optionally behind a two-letter jurisdiction prefix
    // never part of the number.
    if (/^([A-Za-z]{2})?\d+$/.test(compact)) {
        return compact;
    }
    // Non-numeric entries (EP/global refs) keep their shape but must still be
    // escaped - this is user-typed text that ends up rendered as HTML in the
    // chat message list (see Messages.js's html-react-parser usage).
    return entry.replace(/\s+/g, "").replace(/</g, "&lt;").replace(/>/g, "&gt;");
};

const splitCustomFormApplicationNumbers = (raw) => {
    const lines = raw.split(/\r?\n/).filter((line) => line.trim() !== "");
    const entries = lines.length > 1
        ? lines
        : raw
            .replace(US_FORMATTED_APPLICATION_NUMBER, "$1$2$3")
            .replace(INTER_NUMBER_WHITESPACE, ",")
            .split(/[,;]+/);
    return entries
        .map((entry) => normalizeCustomFormApplicationNumber(entry.trim()))
        .filter(Boolean);
};

const checkValidAppNum = (intentName, entries) => {
    switch (intentName) {
        case "PatentBiblioIntent":
        case "OathIntent":
        case "POADocgenIntent":
        case "37CFRDocgenIntent":
        case "Sb30EFSDocgenIntent":
        case "IdsReferenceCountIntent":
        case "Sb30DocgenIntent":
        case "AIA81ADocgenIntent":
        case "RefCheckIntent":
        case "Aia122DocgenIntent":
        case "IdsDocumentDownloadIntent":
        case "AIA83DocgenIntent":
        case "PatentFileWrapperIntent":
        case "Aia123DocgenIntent":
        case "AIA41DocgenIntent":
        case "Sb08Intent":
            return entries.some((entry) => /^\d{8}$/.test(entry));
        default:
            return true;
    }
}

export const simplifyApplicationNumbers = (input, intentName) => {
    if (!input) {
        toast.error(
            "No input detected. Please enter the required information and try again."
        );
        return;
    }

    if (CUSTOM_FORM_INTENTS.includes(intentName?.toLowerCase())) {
        const applicationNumbers = splitCustomFormApplicationNumbers(input);
        if (applicationNumbers.length === 0) {
            toast.error("The application number provided is invalid. Please verify and try again.");
            return;
        }
        return applicationNumbers.join(",");
    }

    const lines = input
    .split(/\r?\n/)
    .filter((line) => line.trim() !== "");
    const errorApplicationNumbers = [];
    const MAX_SB08_892_APPLICATION_NUMBERS = Number(process.env.REACT_APP_JUNE_MAX_SB08_892_APPLICATION_NUMBERS_LIMIT || 100);
    const MAX_OATH_APPLICATION_NUMBERS = Number(process.env.REACT_APP_JUNE_MAX_OATH_APPLICATION_NUMBERS_LIMIT || 10);
    const MAX_IMAGE_FILE_WRAPPER_APPLICATION_NUMBERS = Number(process.env.REACT_APP_JUNE_MAX_IMAGE_FILE_WRAPPER_APPLICATION_NUMBERS_LIMIT || 10);
    const MAX_1449_892_APPLICATION_NUMBERS = Number(process.env.REACT_APP_JUNE_MAX_1449_892_APPLICATION_NUMBERS_LIMIT || 100);

    if (intentName === "ShellOAIntent") {
        if (lines.length > 1) {
            toast.error("Only one application number allowed per request");
            return;
        }
        // if (lines.length === 1 && lines[0].length > 10) {
        //   toast.error("Only one application number allowed per request");
        //   return;
        // }
        const cleanedInput = input.trim().replace(/\n/g, ",");
        // const entries = cleanedInput.trim().split(",");
        const applicationNumbers = [];

        // entries.forEach((entry) => {
        const simplifiedNumber = cleanedInput.trim().replace(/[^0-9]/g, "");
        if (simplifiedNumber && simplifiedNumber.length === 8) {
            applicationNumbers.push(simplifiedNumber);
        } else {
            errorApplicationNumbers.push(cleanedInput);
        }
        // });

        if (applicationNumbers.length === 0 || errorApplicationNumbers.length > 0) {
            toast.error(
                "The input application number(s) is invalid. Please try again with valid input."
            );
            return;
        }
        return applicationNumbers.join(",");
    }
    if ((intentName === "Sb08Intent" || intentName === "RefCheckIntent") && lines.length > 1) {
        toast.error("Only one application number allowed per request");
        return;
    } else if (
        intentName === "OathIntent" &&
        lines.length > MAX_OATH_APPLICATION_NUMBERS
    ) {
        toast.error(
            `Only ${MAX_OATH_APPLICATION_NUMBERS} application numbers allowed per request`
        );
        return;
    } else if (intentName === "IdsDocumentDownloadIntent" && lines.length > MAX_1449_892_APPLICATION_NUMBERS) {
        toast.error(`Only ${MAX_1449_892_APPLICATION_NUMBERS} application numbers allowed per request`);
        return;
    } else if (intentName === "Sb08And892Intent" && lines.length > MAX_SB08_892_APPLICATION_NUMBERS) {
        toast.error(`Only ${MAX_SB08_892_APPLICATION_NUMBERS} application numbers allowed per request`);
        return;
    }
     else if (
        intentName === "PatentFileWrapperIntent" &&
        lines.length > MAX_IMAGE_FILE_WRAPPER_APPLICATION_NUMBERS
    ) {
        toast.error(
            `Please input a max. of ${MAX_IMAGE_FILE_WRAPPER_APPLICATION_NUMBERS} application numbers. Please limit the input and try again`
        );
        return;
    }

    if (lines.length === 1 && lines[0].length > 10) {
        const cleanedInput = input.trim().replace(/\n/g, ",");
        const entries = cleanedInput.trim().split(",");
        const applicationNumbers = [];

        entries.forEach((entry) => {
            const simplifiedNumber = entry.trim().replace(/[^0-9]/g, "");
            if (simplifiedNumber) {
                applicationNumbers.push(simplifiedNumber);
            } else {
                errorApplicationNumbers.push(entry);
            }
        });
        if ((intentName === "Sb08Intent" || intentName === "RefCheckIntent") && applicationNumbers.length > 1) {
            toast.error("Only one application number allowed per request");
            return;
        } else if (
            intentName === "Sb08And892Intent" &&
            applicationNumbers.length > MAX_SB08_892_APPLICATION_NUMBERS
        ) {
            toast.error(
                `Only ${MAX_SB08_892_APPLICATION_NUMBERS} application numbers allowed per request`
            );
            return;
        } else if (
            intentName === "OathIntent" &&
            applicationNumbers.length > MAX_OATH_APPLICATION_NUMBERS
        ) {
            toast.error(
                `Only ${MAX_OATH_APPLICATION_NUMBERS} application numbers allowed per request`
            );
            return;
        } else if (intentName === "IdsDocumentDownloadIntent" && applicationNumbers.length > MAX_1449_892_APPLICATION_NUMBERS) {
            toast.error(`Only ${MAX_1449_892_APPLICATION_NUMBERS} application numbers allowed per request`);
            return;
        } else if (intentName === "Sb08And892Intent" && applicationNumbers.length > MAX_SB08_892_APPLICATION_NUMBERS) {
            toast.error(`Only ${MAX_SB08_892_APPLICATION_NUMBERS} application numbers allowed per request`);
            return;
        }
        else if (
            intentName === "PatentFileWrapperIntent" &&
            applicationNumbers.length > MAX_IMAGE_FILE_WRAPPER_APPLICATION_NUMBERS
        ) {
            toast.error(
                `Please input a max. of ${MAX_IMAGE_FILE_WRAPPER_APPLICATION_NUMBERS} application numbers. Please limit the input and try again`
            );
            return;
        }

        if (applicationNumbers.length === 0) {
            toast.error(
                "The application number provided is invalid. Please verify and try again."
            );
            return;
        }

        if (errorApplicationNumbers.length > 0) {
            toast.error(
                "There are some invalid application numbers provided. Please verify and try again."
            );
            return;
        }

        if (applicationNumbers.length > process.env.REACT_APP_JUNE_APPLICATION_NUMBER_LIMIT) {
            toast.error(
                `The count of numbers should not exceed ${process.env.REACT_APP_JUNE_APPLICATION_NUMBER_LIMIT}.`
            );
            return;
        }
        const hasValidAppNum = checkValidAppNum(intentName, applicationNumbers);
        if (!hasValidAppNum) {
            toast.error("The application number provided is invalid. Please verify and try again.");
            return;
        }

        return applicationNumbers.join(",");
    } else {
        const processedNumbers = lines
            .map((line) => line.replace(/[^0-9]/g, ""))
            .filter((number) => {
                if (number) {
                    return true;
                } else {
                    errorApplicationNumbers.push(number);
                    return false;
                }
            });

        if (processedNumbers.length === 0) {
            toast.error(
                "The application number provided is invalid. Please verify and try again."
            );
            return;
        }

        if (errorApplicationNumbers.length > 0) {
            toast.error(
                "There are some invalid application numbers provided. Please verify and try again."
            );
            return;
        }

        if (processedNumbers.length > process.env.REACT_APP_JUNE_APPLICATION_NUMBER_LIMIT) {
            toast.error(
                `The count of numbers should not exceed ${process.env.REACT_APP_JUNE_APPLICATION_NUMBER_LIMIT}.`
            );
            return;
        }

        const hasValidAppNum = checkValidAppNum(intentName, processedNumbers);
        if (!hasValidAppNum) {
            toast.error("The application number provided is invalid. Please verify and try again.");
            return;
        }

        return processedNumbers.join(",");
    }
};

export const validateFileForIntent = async (intentName, files, slotName = "") => {
    const updatedIntentName = intentName.toLowerCase();

    switch (updatedIntentName) {
        case "37cfrdocgenintent": {
            const allowedFileTypes = ["pdf"];
            const isValid = files.every((file) =>
                allowedFileTypes.includes(file.name.split(".").pop().toLowerCase())
            );
            if (!isValid) {
                toast.error(
                    "Ensure the document is in a supported format (e.g., PDF) and re-upload it."
                );
                return false;
            }
            return true;
        }
        case "shelloaintent": {
            let allowedFileTypes = ["pdf", "docx"];
            if (slotName === "file") {
                allowedFileTypes = ["docx"];
            }
            const isValid = files.every((file) =>
                allowedFileTypes.includes(file.name.split(".").pop().toLowerCase())
            );
            if (!isValid) {
                toast.error(
                    `Ensure the document is in a supported format (e.g., ${allowedFileTypes.join(", ")}) and re-upload it.`
                );
                return false;
            }

            const isZip = files.some(
                (file) => file.name.split(".").pop().toLowerCase() === "zip"
            );

            const otherFiles = files.filter(
                (file) => file.name.split(".").pop().toLowerCase() !== "zip"
            );

            if (isZip && otherFiles.length !== 0) {
                toast.error("Upload either a ZIP file or each file separately, not both.");
                return false;
            }

            if (!isZip) {
                const pairedFiles = files.filter((file) => {
                    const baseName = file.name.split(".").slice(0, -1).join(".");
                    const fileType = file.name.split(".").pop().toLowerCase();

                    if (fileType === "docx" || fileType === "pdf") {
                        return files.some(
                            (otherFile) =>
                                otherFile.name.startsWith(baseName) &&
                                otherFile.name !== file.name &&
                                otherFile.name.split(".").pop().toLowerCase() ===
                                (fileType === "docx" ? "pdf" : "docx") &&
                                (baseName.includes("CTFR") || baseName.includes("CTNF"))
                        );
                    }
                    return false;
                });

                if (!((files.length === 2 && pairedFiles.length === 2 && slotName === "files") || (pairedFiles.length === 0 && files.length === 1 && files[0].name.split(".").pop().toLowerCase() === "docx" && slotName === "file"))) {
                    toast.error(
                        "A required document is missing. Please ensure all mandatory documents are included."
                    );
                    return false;
                }

                // const remainingFiles = files.filter(
                //   (file) => !pairedFiles.includes(file)
                // );
                // if (
                //   remainingFiles.length !== 1 ||
                //   remainingFiles[0].name.split(".").pop().toLowerCase() !== "docx"
                // ) {
                //   toast.error("Missing docx file. Please ensure all mandatory documents are included.");
                //   return false;
                // }

                // if (files.length !== 2) {
                //   toast.error(
                //     `Please upload exactly 3 files to proceed. You have uploaded ${files.length} file(s).`
                //   );
                //   return false;
                // }
                return true;
            }
            // else {
            //   if (files.length !== 1) {
            //     toast.error(
            //       `Please upload at least 1 file to proceed. You have uploaded ${files.length} file(s).`
            //     );
            //     return false;
            //   }
            // }
            // return true;
        }
        case "referenceextraction": {
            const allowedFileTypes = ["docx", "pdf", "doc"];
            const isValid = files.every((file) =>
                allowedFileTypes.includes(file.name.split(".").pop().toLowerCase())
            );
            if (!isValid) {
                toast.error(
                    "Ensure the document is in a supported format (e.g., PDF, DOCX, DOC) and re-upload it."
                );
                return false;
            }

            const MAX_TOTAL_FILES_SIZE_MB = process.env.REF_EXT_MAX_SIZE ?? 25;

            // Validation
            if (files.length < 1) {
                toast.error("Upload DOC/DOCX or PDF files");
                return false;
            }

            if (files.length > 0) {
                const totalFilesSize = files.reduce(
                    (total, file) => total + file.size,
                    0
                );
                if (totalFilesSize > MAX_TOTAL_FILES_SIZE_MB * 1024 * 1024) {
                    toast.error(
                        `The files total size should not exceed ${MAX_TOTAL_FILES_SIZE_MB} MB.`
                    );
                    return false;
                }
            }
            return true;
        }
        case "flattenpdfintent": {
            const allowedFileTypes = ["zip", "pdf"];
            const isValid = files.every((file) =>
                allowedFileTypes.includes(file.name.split(".").pop().toLowerCase())
            );
            if (!isValid) {
                toast.error(
                    "Ensure the document is in a supported format (e.g., PDF, ZIP) and re-upload it."
                );
                return false;
            }

            const MAX_ZIP_SIZE_MB = parseInt(process.env.REACT_APP_FLATTEN_PDF_MAX_ZIP_SIZE_MB || "50");
            const MAX_PDF_COUNT = parseInt(process.env.REACT_APP_FLATTEN_PDF_MAX_PDF_COUNT || "15");
            const MAX_TOTAL_PDF_SIZE_MB = parseInt(process.env.REACT_APP_FLATTEN_PDF_MAX_TOTAL_PDF_SIZE_MB || "50");

            let zipFile = [];
            let pdfFiles = [];

            // Separate files by type
            files.forEach((file) => {
                const fileType = file.name.split(".").pop().toLowerCase();
                if (fileType === "zip") {
                    zipFile.push(file);
                } else if (fileType === "pdf") {
                    pdfFiles.push(file);
                }
            });

            // Validation
            if (zipFile.length > 0 && pdfFiles.length > 0) {
                toast.error("Upload either a ZIP file or PDF files, not both.");
                return false;
            }

            if (zipFile.length > 0) {
                if (zipFile.length > 1) {
                    toast.error("Please upload only one ZIP file.");
                    return false;
                }
                if (zipFile[0].size > MAX_ZIP_SIZE_MB * 1024 * 1024) {
                    toast.error(
                        `The ZIP file size should not exceed ${MAX_ZIP_SIZE_MB} MB.`
                    );
                    return false;
                }
            } else if (pdfFiles.length > 0) {
                if (pdfFiles.length > MAX_PDF_COUNT) {
                    toast.error(
                        `The number of PDF files should not exceed ${MAX_PDF_COUNT}.`
                    );
                    return false;
                }

                const totalPdfSize = pdfFiles.reduce(
                    (total, file) => total + file.size,
                    0
                );
                if (totalPdfSize > MAX_TOTAL_PDF_SIZE_MB * 1024 * 1024) {
                    toast.error(
                        `The total size of PDF files should not exceed ${MAX_TOTAL_PDF_SIZE_MB} MB.`
                    );
                    return false;
                }
            } else {
                toast.error("Upload either a ZIP file or PDF files.");
                return false;
            }

            return true;
        }

        case "sb08intent": {
            if (files.length !== 1 || files[0].name.split(".").pop().toLowerCase() !== "xlsx") {
                toast.error("Please upload a single Excel (.xlsx) file.");
                return false;
            }
            return true;
        }

        case "poadocgenintent": {
            if (files.length !== 1) {
                toast.error("Please upload a maximum of 1 file");
                return false;
            }
            const allowedFileTypes = ["pdf"];
            const isValid = files.every((file) =>
                allowedFileTypes.includes(file.name.split(".").pop().toLowerCase())
            );
            if (!isValid) {
                toast.error(
                    "Ensure the document is in a supported format (e.g., PDF) and re-upload it."
                );
                return false;
            }
            return true;
        }

        case "claimsformatterintent": {
            const allowedFileTypes = ["docx"];
            const isValid = files.every((file) =>
                allowedFileTypes.includes(file.name.split(".").pop().toLowerCase())
            );
            if (!isValid) {
                toast.error(
                    "Ensure the document is in a supported format (e.g., DOCX) and re-upload it."
                );
                return false;
            }

            const MAX_TOTAL_FILES_SIZE_MB = process.env.REF_EXT_MAX_SIZE ?? 25;

            // Validation
            if (files.length < 1) {
                toast.error("Upload DOCX file.");
                return false;
            }

            if (files.length > 0) {
                const totalFilesSize = files.reduce(
                    (total, file) => total + file.size,
                    0
                );
                if (totalFilesSize > MAX_TOTAL_FILES_SIZE_MB * 1024 * 1024) {
                    toast.error(
                        `The files total size should not exceed ${MAX_TOTAL_FILES_SIZE_MB} MB.`
                    );
                    return false;
                }
            }

            if (files.length > 1) {
                toast.error("Please upload only one DOCX file.");
                return false;
            }
            return true;
        }

        default: {
            return true;
        }
    }
};

export const sanitizeFileName = (originalName) => {
    const parts = originalName.split(".");
    const extension = parts.length > 1 ? parts.pop() : "";
    const baseName = parts.join(".");

    const sanitizedBase = baseName.replace(/[^a-zA-Z0-9-_]/g, "");
    return extension ? `${sanitizedBase}.${extension}` : sanitizedBase;
};

export const getCurrentTime = () =>
    new Date().toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });